import { Typography } from '@mui/material';
import { BackupStatus, ConditionOperatorEnum } from '@repo/api-gw-sdk';
import type { ResourceType, Condition } from '@repo/api-gw-sdk';
import type { PaginationState, SortingState } from '@tanstack/react-table';
import { useState } from 'react';

import { createBackupStatusNotInCondition } from '@/components/backupStatus/createBackupStatusNotInCondition';
import { id, resourceType } from '@/components/queryBuilder/properties';
import { ColumnSelector } from '@/components/table/columnsPopup';
import { StepContainer } from '@/components/wizard/StepContainer';
import type { StepProps } from '@/components/wizard/StepProps';
import { RenameEntityName } from '@/components/wizard/renameEntityName';
import { isAnAllCondition } from '@/types/advanceFilter';

import { isRestrictedTypesPolicy } from './BackupPolicyConditionEditor';
import type { BackupPolicyEditState } from './BackupPolicyCreationFlow';
import { CustomPolicySupportedDisclaimer } from './customPolicySupportedDisclaimer';

import { InventoryListTable } from '../../inventory/inventoryListTable';

const createCustomPolicySupportedTypes = (
  supportedTypes?: ResourceType[]
): Condition[] => {
  if (!supportedTypes?.length) {
    return [];
  }
  return [
    {
      property: resourceType.name,
      operator: ConditionOperatorEnum.In,
      value: supportedTypes,
    },
  ];
};

export const BackupPolicyResourcesList = (
  props: StepProps<BackupPolicyEditState>
) => {
  const conditions =
    props.wizardState.resourcesScopeType === 'allIncluded' ||
    props.wizardState.resourcesScopeType === 'allExcluded' ||
    !props.wizardState.condition ||
    isAnAllCondition(props.wizardState.condition)
      ? []
      : [props.wizardState.condition];

  const resourceTypesCondition = isRestrictedTypesPolicy(
    props.wizardState.policyType
  )
    ? createCustomPolicySupportedTypes(props.wizardState.resourceTypes)
    : [];

  const backupStatusNotInCondition = createBackupStatusNotInCondition([
    BackupStatus.ExcludedFromBackup,
    BackupStatus.InitialClassification,
    BackupStatus.Terminated,
    BackupStatus.Disconnected,
    BackupStatus.Unsupported,
    BackupStatus.LimitExceeded,
  ]);

  let baseCondition: Condition = {
    conditions: [
      ...conditions,
      ...resourceTypesCondition,
      backupStatusNotInCondition,
    ],
    operator: ConditionOperatorEnum.And,
  };

  if (
    (props.wizardState.include?.length &&
      props.wizardState.resourcesScopeType !== 'allIncluded') ||
    props.wizardState.resourcesScopeType === 'allExcluded'
  ) {
    const includeCondition = {
      operator: ConditionOperatorEnum.In,
      property: 'resourceId',
      value: props.wizardState.include,
    };
    baseCondition = {
      operator: ConditionOperatorEnum.And,
      conditions: [baseCondition, includeCondition],
    };
  }

  if (
    props.wizardState.exclude?.length &&
    props.wizardState.resourcesScopeType !== 'allExcluded'
  ) {
    const exclude = {
      operator: ConditionOperatorEnum.NotIn,
      property: 'resourceId',
      value: props.wizardState.exclude,
    };

    baseCondition = {
      operator: ConditionOperatorEnum.And,
      conditions: [baseCondition, exclude],
    };
  }

  return (
    <>
      <StepContainer
        stepperLabels={props.stepperLabels}
        stepperIndex={1}
        onBackClick={props.back}
        canGoNext={() => true}
        onNextClick={async () => {
          props.setNextStep(props.currentStep.next?.[0]);
        }}
        sx={{ padding: 0 }}
        navigationComponent={
          <RenameEntityName
            label='Backup policy name'
            name={props.wizardState.name}
            onSubmit={(name) =>
              props.setWizardState((state) => ({ ...state, name }))
            }
          />
        }
      >
        <CustomPolicySupportedDisclaimer
          policyType={props.wizardState.policyType}
          vaultManagedType={props.wizardState.vaultManagedType}
        />
        <Typography className='text-center leading-5 my-[20px]'>
          These are the resources that meet the conditions you set. Review the
          list to make sure it contains the resources you expected.
        </Typography>
        <InventoryTable query={baseCondition} />
      </StepContainer>
    </>
  );
};

const InventoryTable = (props: { query?: Condition }) => {
  const [sorting, setSorting] = useState<SortingState>([
    { id: id.name, desc: false },
  ]);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 25,
  });

  return (
    <InventoryListTable
      isMultiSelect={false}
      query={props.query}
      showFiltersBar={false}
      sorting={sorting}
      pagination={pagination}
      onPaginationChange={setPagination}
      onSortingChange={(id, desc) => setSorting([{ id, desc }])}
      paginationBarActions={[ColumnSelector]}
    />
  );
};
